// src/pages/Enseignants/Attendance/AdherentGrid.jsx
import React, { useEffect } from "react";
import { FaCheck, FaTimes, FaBirthdayCake } from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";

const BirthdayTag = () => (
  <div className="absolute top-2 right-2">
    <FaBirthdayCake className="text-white bg-fuchsia-600 rounded-full p-2 w-10 h-10 shadow" />
  </div>
);

const AssiduityTag = ({ info }) => {
  if (!info) return null;
  const pct = Math.round(info.assiduity ?? 0);
  const pres = info.totalAttendances ?? 0;
  const tot = info.total ?? 1;
  const color =
    pct >= 75
      ? "bg-emerald-500"
      : pct >= 50
      ? "bg-amber-500"
      : pct > 0
      ? "bg-rose-500"
      : "bg-blue-500";
  return (
    <div
      className={`absolute top-2 left-2 ${color} text-white px-2 py-1 rounded text-[11px] font-semibold`}
    >
      {pct}% ({pres}/{tot})
    </div>
  );
};

const NameBar = ({ nom, prenom }) => (
  <div className="px-2 py-1 text-center bg-gray-900">
    <div className="m-0 text-[11px] leading-[1.05] font-medium text-white dark:text-slate-100 line-clamp-2 max-h-[2.5em] py-1">
      {(nom || "").toUpperCase()} {prenom || ""}
    </div>
  </div>
);

const AdherentCard = ({
  a,
  attendanceStatus,
  onToggle,
  assiduityInfo,
  birthdaySet,
  getDefaultPhoto,
}) => {
  const injured = !!a.is_blessed;
  const info = assiduityInfo[a.id];
  const isBirthday = birthdaySet.has(a.id);

  // Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <button
      type="button"
      className="relative rounded-lg overflow-hidden ring-1 ring-slate-200 dark:ring-slate-800 bg-white dark:bg-slate-900 text-left"
      onClick={() => onToggle(a.id)}
      title="Basculer présence"
    >
      <figure className="aspect-square">
        <img
          src={toAbsolute(a.photo_url) || getDefaultPhoto(a.genre)}
          alt={`${a.nom} ${a.prenom}`}
          className="object-cover w-full h-full"
          draggable={false}
        />
      </figure>

      {isBirthday && <BirthdayTag />}
      <AssiduityTag info={info} />

      {injured && (
        <div className="absolute top-14 left-2">
          <MdMedicalServices className="text-white bg-rose-600 rounded-full p-2 w-10 h-10 shadow" />
        </div>
      )}

      {/* overlays état sélection */}
      {attendanceStatus === 1 && (
        <div className="absolute inset-0 bg-emerald-500/50 flex items-center justify-center">
          <FaCheck className="text-white text-3xl" />
        </div>
      )}
      {attendanceStatus === 2 && (
        <div className="absolute inset-0 bg-rose-500/50 flex items-center justify-center">
          <FaTimes className="text-white text-3xl" />
        </div>
      )}

      <NameBar nom={a.nom} prenom={a.prenom} />
    </button>
  );
};

/** Grille SANS pagination — charge et affiche tout */
const AdherentGrid = ({
  adherents,
  attendance,
  onToggle,
  assiduityInfo,
  requestAssiduity,
  birthdaySet,
  getDefaultPhoto,
}) => {
  // Charger l’assiduité pour tous les adhérents affichés
  useEffect(() => {
    if (Array.isArray(adherents) && adherents.length > 0) {
      requestAssiduity?.(adherents.map((a) => a.id));
    }
  }, [adherents, requestAssiduity]);

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold">
          {adherents.length} adhérent{adherents.length > 1 ? "s" : ""} trouvé
          {adherents.length > 1 ? "s" : ""}
        </h3>
        {/* plus de pagination, donc pas d’info de page */}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {adherents.map((a) => (
          <AdherentCard
            key={a.id}
            a={a}
            attendanceStatus={attendance[a.id] ?? 0}
            onToggle={onToggle}
            assiduityInfo={assiduityInfo}
            birthdaySet={birthdaySet}
            getDefaultPhoto={getDefaultPhoto}
          />
        ))}
      </div>
    </>
  );
};

export default AdherentGrid;
